import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { HiOutlineDocumentText, HiOutlineMagnifyingGlass, HiOutlinePlus, HiOutlineSquares2X2 } from "react-icons/hi2";

import Modal from "@/components/modal";
import LoadingButton from "@/components/loadingButton";
import Loader from "@/components/loader";
import api from "@/services/api";

// Color palette for tags
const tagColors = [
  { bg: "bg-blue-100", text: "text-blue-700" },
  { bg: "bg-green-100", text: "text-green-700" },
  { bg: "bg-purple-100", text: "text-purple-700" },
  { bg: "bg-pink-100", text: "text-pink-700" },
  { bg: "bg-yellow-100", text: "text-yellow-700" },
  { bg: "bg-indigo-100", text: "text-indigo-700" },
  { bg: "bg-red-100", text: "text-red-700" },
  { bg: "bg-teal-100", text: "text-teal-700" },
];

// Function to get consistent color for a tag
const getTagColor = (tag) => {
  const index = tag.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return tagColors[index % tagColors.length];
};

export default function List() {
  const [careerPaths, setCareerPaths] = useState([]);
  const [filter, setFilter] = useState({ search: "" });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = useSelector((state) => state.Auth.user);
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  useEffect(() => {
    fetchCareerPaths();
  }, [filter]);

  const fetchCareerPaths = async () => {
    try {
      const { ok, data } = await api.post("/career_path/search", filter);
      if (ok) setCareerPaths(data);
    } catch (e) {
      toast.error("Error loading career paths");
    }
    setLoading(false);
  };

  if (loading) return <Loader />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto p-6">
        <div className="flex items-center justify-between mb-8 gap-4 max-w-screen-2xl mx-auto">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Career Paths</h1>
            <p className="text-gray-600">Discover and explore {careerPaths.length} available career paths</p>
          </div>
            <div className="flex items-center justify-end gap-3">
            <div className="relative">
              <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 w-64"
                placeholder="Search career paths..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              />
            </div>
            {isAdmin && (
              <button
                onClick={() => navigate("/selecole/career-path/organize")}
                className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 px-5 py-2.5 rounded-xl text-sm font-medium border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                <HiOutlineSquares2X2 className="w-4 h-4" />
                Organize
              </button>
            )}
            <Create />
          </div>
        </div>

        {careerPaths.length === 0 ? (
          <div className="text-center py-16 max-w-screen-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12">
              <HiOutlineDocumentText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No career paths found</h3>
              <p className="text-gray-500 mb-6">Get started by creating your first career path</p>
              <Create />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-screen-2xl mx-auto">
            {careerPaths.map((path) => (
              <div
                key={path._id}
                className="group bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-gray-300 transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => navigate(`/selecole/career-path/${path._id}`)}>
                <div className="flex h-72">
                  <div className="relative w-2/5 bg-gray-50 rounded-l-lg overflow-hidden flex-shrink-0">
                    {path.image ? (
                      <img 
                        src={path.image} 
                        alt={path.name} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                          const fallback = document.createElement('div');
                          fallback.className = 'text-center';
                          fallback.innerHTML = '<div class="text-4xl mb-2">🎯</div><div class="text-xs text-gray-400 font-medium">Career Path</div>';
                          e.target.parentElement.appendChild(fallback);
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-4xl mb-2">🎯</div>
                          <div className="text-xs text-primary-500 font-medium">Career Path</div>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm">
                        🎯 Certification
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 p-6 flex flex-col min-w-0">
                    <h3 className="font-semibold text-gray-900 text-lg mb-3 group-hover:text-primary-700 transition-colors duration-200 line-clamp-2">{path.name}</h3>
                    {path.description && <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">{path.description}</p>}
                    {path.tags && path.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {path.tags.map((tag) => {
                          const { bg, text } = getTagColor(tag);
                          return (
                            <span 
                              key={tag} 
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${text} transition-colors duration-200`}
                            >
                              {tag}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                      <div className="flex items-center text-sm text-gray-500">
                        <HiOutlineDocumentText className="w-5 h-5 mr-2 text-primary-500" />
                        <span>{path.trainings?.length || 0} modules</span>
                      </div>
                      <div className="text-sm text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">→</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Create = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);

  const onCreate = async () => {
    try {
      setBtnLoading(true);
      const { ok, data } = await api.post("/career_path", values);

      if (!ok) return toast.error("Error creating career path.");

      toast.success("Career path created successfully!");
      setOpen(false);
      setValues({});
      navigate(`/selecole/career-path/${data._id}`);
    } catch (e) {
      toast.error("Error creating career path.");
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div>
      <button
        className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl text-sm font-medium shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
        onClick={() => setOpen(true)}>
        <HiOutlinePlus className="w-4 h-4" />
        Create Path
      </button>
      <Modal isOpen={open} className="!w-[90vw] md:!w-[600px] !rounded-2xl" onClose={() => setOpen(false)}>
        <div className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <HiOutlinePlus className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Create New Career Path</h1>
            <p className="text-gray-600">Start building a new learning journey</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">Career Path Name *</label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                value={values.name || ""}
                onChange={(e) => setValues({ ...values, name: e.target.value })}
                placeholder="e.g., Full Stack Development, Data Science..."
              />
            </div>
          </div>

          <div className="flex justify-end items-center gap-3 mt-8 pt-6 border-t border-gray-100">
            <button
              className="px-6 py-2.5 text-gray-700 hover:text-gray-900 font-medium rounded-xl hover:bg-gray-100 transition-colors duration-200"
              onClick={() => setOpen(false)}>
              Cancel
            </button>
            <LoadingButton
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-xl font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              loading={btnLoading}
              disabled={!values.name}
              onClick={onCreate}>
              Create Career Path
            </LoadingButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};
